// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Create flags to prevent duplicate operations
let isClearingCaches = false;
let isRegistering = false;

// Clear all service worker caches
async function clearCaches() {
  if ('caches' in window && !isClearingCaches) {
    isClearingCaches = true;
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log(`Clearing cache: ${cacheName}`);
          return caches.delete(cacheName);
        })
      );
      console.log('All caches cleared');
    } catch (err) {
      console.error('Error clearing caches:', err);
    } finally {
      isClearingCaches = false;
    }
  }
}

export function register(config) {
  // Skip registration if already in progress or in safe mode
  if (isRegistering || window.localStorage.getItem('kappasig_safe_mode') === 'true') {
    console.log('Safe mode enabled or registration already in progress - skipping service worker registration');
    return;
  }

  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    isRegistering = true;
    
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);

    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      isRegistering = false;
      return;
    }

    // Wait for the page to load completely
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/serviceWorker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
          isRegistering = false;
        }).catch(err => {
          console.error('Error in serviceWorker.ready:', err);
          isRegistering = false;
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
    
    // Remove the failsafe timeout that could cause race conditions
    // by trying to register the service worker twice
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          isRegistering = false;
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
            isRegistering = false;
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
      // If service worker registration fails, try to unregister any existing one
      // as it might be corrupt
      unregister();
      isRegistering = false;
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Special handling for Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      
      // Accept text/html as valid for service worker for certain server configs
      if (
        response.status === 404 ||
        (contentType != null && 
         contentType.indexOf('javascript') === -1 && 
         contentType.indexOf('text/html') === -1) // Allow text/html
      ) {
        // No service worker found. Probably a different app. Reload the page.
        console.log('checkValidServiceWorker: serviceWorker.js not found or invalid content type. Reloading...');
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          }).catch(err => {
            console.error('Error unregistering service worker:', err);
            isRegistering = false;
          });
        }).catch(err => {
          console.error('Error with service worker ready:', err);
          isRegistering = false;
          window.location.reload();
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
      
      if (isSafari) {
        console.log('Running on Safari in offline mode, service worker may have limitations');
        // For Safari, we can try to continue with registration anyway
        registerValidSW(swUrl, config);
      } else {
        isRegistering = false;
      }      
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
      
    // Also attempt to clear all caches
    clearCaches();
  }
}

/**
 * Updates the service worker by unregistering and re-registering
 * Can be called when the app is stuck to try to fix issues
 */
export function updateServiceWorker(config) {
  if ('serviceWorker' in navigator) {
    // First clear all caches
    clearCaches().then(() => {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        if (registrations.length === 0) {
          // If no service workers found, just reload
          window.location.reload();
          return;
        }
        
        // Create a promise for each registration
        const unregisterPromises = registrations.map(registration => {
          return registration.unregister();
        });
        
        // Wait for all unregistrations to complete, then reload
        Promise.all(unregisterPromises)
          .then(() => {
            console.log('All service workers unregistered');
            window.location.reload();
          })
          .catch(error => {
            console.error('Error unregistering service workers:', error);
            // Force reload anyway
            window.location.reload();
          });
      });
    });
  }
}

/**
 * Completely reset the app by clearing caches and storage
 * This is a nuclear option for when the app is completely stuck
 */
export function resetApp() {
  // Clear all service worker caches
  clearCaches();

  // Unregister service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      const unregisterPromises = registrations.map(registration => registration.unregister());
      Promise.all(unregisterPromises).then(() => {
        console.log('All service workers unregistered');
        
        // Clear localStorage (except safe mode flag)
        const safeMode = localStorage.getItem('kappasig_safe_mode');
        localStorage.clear();
        if (safeMode) {
          localStorage.setItem('kappasig_safe_mode', safeMode);
        }
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear IndexedDB databases
        if (window.indexedDB) {
          const databases = indexedDB.databases ? indexedDB.databases() : Promise.resolve([]);
          databases.then(dbs => {
            dbs.forEach(db => {
              try {
                indexedDB.deleteDatabase(db.name);
                console.log(`Deleted IndexedDB database: ${db.name}`);
              } catch (e) {
                console.error(`Failed to delete IndexedDB database: ${db.name}`, e);
              }
            });
          }).catch(err => {
            console.error('Error listing IndexedDB databases:', err);
          });
        }
        
        // Force page reload to a clean state
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 500);
      });
    });
  } else {
    // If no service worker support, just reload
    window.location.reload();
  }
} 